import { RefObject } from 'react';
import {
  EXPAND_SCROLL_MS,
  GRID_ROW_HEIGHT,
  SMOOTH_DURATION_MS,
  SMOOTH_FINAL_LEG_PX,
} from '../constants';
import { animateScrollTop } from './animateScrollTop';

// Two-stage scroll-to-row that mirrors useGridScrollBehavior, but exposes an
// onComplete callback so the consumer can defer side effects (e.g. expanding
// the row) until the animation lands. Expanding a row mid-animation re-renders
// rowExpansionTemplate while the smooth scroll is in flight, which shifts the
// row out of the viewport.
//
// Behavior:
// - distance <= SMOOTH_FINAL_LEG_PX: smooth scroll only.
// - distance >  SMOOTH_FINAL_LEG_PX: instant pre-scroll near the target, then
//   a short smooth leg so the user still perceives a scroll motion.
// - Retries until virtualScroller has any scrollable height.
export const scrollGridToRowSmooth = (
  gridRef: RefObject<HTMLDivElement | any>,
  index: number,
  onComplete?: () => void,
  smoothWholeDistance: boolean = false
): void => {
  if (!gridRef.current || index < 0) {
    onComplete?.();
    return;
  }
  if (index === 0) {
    // Top of grid — no scroll needed, fire callback now.
    onComplete?.();
    return;
  }

  const targetTop = index * GRID_ROW_HEIGHT;

  const attemptScroll = (attempt: number, prevScrollHeight: number) => {
    const container =
      gridRef.current?.querySelector('.p-virtualscroller') ||
      gridRef.current?.querySelector('.p-datatable-wrapper');

    if (!container || container.scrollHeight <= container.clientHeight) {
      if (attempt < 40) {
        setTimeout(
          () => attemptScroll(attempt + 1, container?.scrollHeight ?? 0),
          25
        );
        return;
      }
      onComplete?.();
      return;
    }

    // The virtual scroller may still be expanding its content height right after
    // a data load. Wait until the target row fits in the scrollable height, or
    // until the height stops growing (target is in the final viewport → only
    // clampable), so we don't clamp the scroll short mid-expansion.
    const targetReachable =
      container.scrollHeight - container.clientHeight >= targetTop;
    const heightStable = container.scrollHeight === prevScrollHeight;
    if (!targetReachable && !heightStable && attempt < 40) {
      setTimeout(() => attemptScroll(attempt + 1, container.scrollHeight), 25);
      return;
    }

    const maxScroll = container.scrollHeight - container.clientHeight;
    const clampedTop = Math.min(targetTop, maxScroll);
    const distance = Math.abs(clampedTop - container.scrollTop);

    // Pixel-snap the target row to the top edge after the estimate-based scroll
    // lands: itemSize estimates and an expanded row drift the computed offset.
    // The target row carries .singleSelectRow; align its top to the row area top
    // (below the sticky header). Near-bottom rows stay clamped — scrollBy can't
    // exceed maxScroll, so they remain as close to the top as possible.
    const snapToTop = () => {
      const row: HTMLElement | null =
        container.querySelector('.singleSelectRow');
      if (!row) return;
      const header: HTMLElement | null =
        container.querySelector('.p-datatable-thead');
      const headerHeight = header?.getBoundingClientRect().height ?? 0;
      const delta =
        row.getBoundingClientRect().top -
        (container.getBoundingClientRect().top + headerHeight);
      if (Math.abs(delta) > 1) {
        container.scrollBy({ top: delta, behavior: 'auto' });
      }
    };

    // Row expansion opts in: the expansion re-renders (spacer height + content
    // transform) cancel a native smooth scroll on the same element, so tween
    // scrollTop by hand instead. Aim at the target row's measured top when it is
    // already rendered (the clicked row is on-screen) so there is no final hop;
    // fall back to the itemSize estimate, with snapToTop as a settle correction.
    if (smoothWholeDistance) {
      const header: HTMLElement | null =
        container.querySelector('.p-datatable-thead');
      const headerHeight = header?.getBoundingClientRect().height ?? 0;
      const row: HTMLElement | null =
        container.querySelector('.singleSelectRow');
      const measuredTop = row
        ? container.scrollTop +
          (row.getBoundingClientRect().top -
            (container.getBoundingClientRect().top + headerHeight))
        : targetTop - headerHeight;
      const to = Math.max(0, Math.min(maxScroll, measuredTop));
      animateScrollTop(container, to, EXPAND_SCROLL_MS, () => {
        snapToTop();
        onComplete?.();
      });
      return;
    }

    if (distance <= SMOOTH_FINAL_LEG_PX) {
      container.scrollTo({ top: clampedTop, behavior: 'smooth' });
      setTimeout(() => {
        snapToTop();
        onComplete?.();
      }, SMOOTH_DURATION_MS);
      return;
    }

    const direction = clampedTop > container.scrollTop ? 1 : -1;
    const preScrollTop = clampedTop - direction * SMOOTH_FINAL_LEG_PX;
    container.scrollTo({
      top: Math.max(0, Math.min(maxScroll, preScrollTop)),
      behavior: 'auto',
    });
    setTimeout(() => {
      container.scrollTo({ top: clampedTop, behavior: 'smooth' });
      setTimeout(() => {
        snapToTop();
        onComplete?.();
      }, SMOOTH_DURATION_MS);
    }, 250);
  };

  setTimeout(() => attemptScroll(0, 0), 1);
};
